import React from 'react';
import PDFReportGenerator from '../components/reports/PDFReportGenerator';

/**
 * PDF Service for generating and downloading contractor comparison reports
 * Enhanced with better error handling and React 19 compatibility
 */
class PDFService {
  
  /**
   * Generate and download PDF report
   * @param {Object} reportData - The data for the PDF report
   * @param {Object} reportData.recommendationData - Recommendation data
   * @param {Object} reportData.evaluationData - Evaluation data  
   * @param {Object} reportData.consolidatedData - Consolidated contractor data
   * @param {Object} reportData.guestAnalysisData - Guest analysis data
   * @param {string} reportData.userPlan - User plan type
   * @param {boolean} reportData.hasActiveSubscription - Active subscription status
   * @param {string} filename - Optional custom filename
   */
  static async generateAndDownloadPDF(reportData, filename = null) {
    let loadingToast = null;
    
    try {
      console.log('Starting PDF generation with data:', reportData);
      
      // Show loading state
      loadingToast = this.showLoadingToast();
      
      // Check if @react-pdf/renderer is available
      let pdf;
      try {
        const reactPdf = await import('@react-pdf/renderer');
        pdf = reactPdf.pdf;
        console.log('@react-pdf/renderer loaded successfully');
      } catch (importError) {
        console.error('@react-pdf/renderer import failed:', importError);
        this.hideLoadingToast(loadingToast);
        this.showErrorToast('PDF library not available. Please refresh the page and try again.');
        return;
      }
      
      // Validate reportData
      if (!reportData || (!reportData.consolidatedData && !reportData.guestAnalysisData)) {
        throw new Error('Invalid report data provided');
      }
      
      console.log('Creating PDF document component...');
      
      // Create the PDF document with proper React.createElement
      const MyDocument = React.createElement(PDFReportGenerator, {
        ...reportData,
        key: 'pdf-document-' + Date.now() // Add unique key
      });
      
      console.log('Generating PDF blob...');
      
      // Generate the PDF blob with timeout
      const blobPromise = pdf(MyDocument).toBlob();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('PDF generation timed out')), 30000)
      );
      
      const blob = await Promise.race([blobPromise, timeoutPromise]);
      
      console.log('PDF blob generated successfully, size:', blob.size);
      
      // Generate filename if not provided
      const defaultFilename = this.generateFilename(reportData);
      const finalFilename = filename || defaultFilename;
      
      // Download the PDF using native browser API
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFilename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      
      // Hide loading state and show success
      this.hideLoadingToast(loadingToast);
      this.showSuccessToast('PDF report downloaded successfully!');
      
      // Track analytics event
      this.trackPDFDownload(reportData);
      
      console.log('PDF download completed successfully');
      return blob;
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      if (loadingToast) {
        this.hideLoadingToast(loadingToast);
      }
      
      // Show specific error messages
      let errorMessage = 'Failed to generate PDF report. ';
      if (error.message.includes('timeout')) {
        errorMessage += 'The operation took too long. Please try again.';
      } else if (error.message.includes('import')) {
        errorMessage += 'PDF library not loaded. Please refresh the page.';
      } else if (error.message.includes('Invalid report data')) {
        errorMessage += 'Report data is missing. Please try refreshing the analysis.';
      } else {
        errorMessage += 'Please try again.';
      }
      
      this.showErrorToast(errorMessage);
      throw error;
    }
  }
  
  /**
   * Generate PDF without downloading (for preview)
   * @param {Object} reportData - The data for the PDF report
   * @returns {Promise<Blob>} PDF blob
   */
  static async generatePDFBlob(reportData) {
    try {
      // Check if @react-pdf/renderer is available
      const { pdf } = await import('@react-pdf/renderer');
      
      const MyDocument = React.createElement(PDFReportGenerator, {
        ...reportData,
        key: 'pdf-preview-' + Date.now()
      });
      
      return await pdf(MyDocument).toBlob();
    } catch (error) {
      console.error('Error generating PDF blob:', error);
      throw error;
    }
  }
  
  /**
   * Generate a filename for the PDF report
   * @param {Object} reportData - The report data
   * @returns {string} Generated filename
   */
  static generateFilename(reportData) {
    const date = new Date().toISOString().split('T')[0];
    const contractorCount = this.getContractorCount(reportData);
    const planType = reportData.userPlan || 'free';
    
    return `Trakvu_Proposal_Analysis_${contractorCount}Contractors_${planType}_${date}.pdf`;
  }
  
  /**
   * Get the number of contractors in the report
   * @param {Object} reportData - The report data
   * @returns {number} Number of contractors
   */
  static getContractorCount(reportData) {
    // Check guest data first
    if (reportData.guestAnalysisData) {
      const actualData = reportData.guestAnalysisData.guestAnalysis || reportData.guestAnalysisData;
      let count = 0;
      if (actualData.Contractor_1_Information) count++;
      if (actualData.Contractor_2_Information) count++;
      return count;
    }
    
    // Check consolidated data
    if (reportData.consolidatedData) {
      return [1, 2, 3, 4, 5]
        .map(num => reportData.consolidatedData[`contractor_${num}`])
        .filter(Boolean)
        .length;
    }
    
    return 0;
  }
  
  /**
   * Show loading toast notification
   * @returns {string} Toast ID for hiding later
   */
  static showLoadingToast() {
    // You can integrate with your existing toast system here
    // For now, we'll use a simple approach
    const toastId = 'pdf-loading-' + Date.now();
    
    // Create a simple loading indicator
    const loadingDiv = document.createElement('div');
    loadingDiv.id = toastId;
    loadingDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #0077cc;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: Inter, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    loadingDiv.innerHTML = `
      <div style="width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-top: 2px solid white; border-radius: 50%; animation: spin 1s linear infinite;"></div>
      Generating PDF report...
    `;
    
    // Add spin animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(loadingDiv);
    return toastId;
  }
  
  /**
   * Hide loading toast notification
   * @param {string} toastId - Toast ID to hide
   */
  static hideLoadingToast(toastId) {
    const loadingDiv = document.getElementById(toastId);
    if (loadingDiv) {
      loadingDiv.remove();
    }
  }
  
  /**
   * Show success toast notification
   * @param {string} message - Success message
   */
  static showSuccessToast(message) {
    const toastId = 'pdf-success-' + Date.now();
    
    const successDiv = document.createElement('div');
    successDiv.id = toastId;
    successDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: Inter, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    successDiv.innerHTML = `
      <i class="fas fa-check-circle"></i>
      ${message}
    `;
    
    document.body.appendChild(successDiv);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      successDiv.remove();
    }, 3000);
  }
  
  /**
   * Show error toast notification
   * @param {string} message - Error message
   */
  static showErrorToast(message) {
    const toastId = 'pdf-error-' + Date.now();
    
    const errorDiv = document.createElement('div');
    errorDiv.id = toastId;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: Inter, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
    `;
    errorDiv.innerHTML = `
      <i class="fas fa-exclamation-circle"></i>
      ${message}
    `;
    
    document.body.appendChild(errorDiv);
    
    // Auto remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
  
  /**
   * Track PDF download analytics
   * @param {Object} reportData - The report data
   */
  static trackPDFDownload(reportData) {
    try {
      // Track analytics event - you can integrate with your analytics service
      const eventData = {
        event: 'pdf_download',
        contractor_count: this.getContractorCount(reportData),
        user_plan: reportData.userPlan,
        has_subscription: reportData.hasActiveSubscription,
        is_guest_data: !!(reportData.guestAnalysisData && (!reportData.consolidatedData || reportData.userPlan === 'free')),
        timestamp: new Date().toISOString()
      };
      
      // Send to analytics service (replace with your actual analytics implementation)
      console.log('PDF Download Analytics:', eventData);
      
      // Example: Google Analytics 4
      if (window.gtag) {
        window.gtag('event', 'pdf_download', {
          custom_parameter_1: eventData.contractor_count,
          custom_parameter_2: eventData.user_plan,
          custom_parameter_3: eventData.has_subscription
        });
      }
      
      // Example: Custom analytics API
      // await fetch('/api/analytics/track', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(eventData)
      // });
      
    } catch (error) {
      console.error('Analytics tracking error:', error);
      // Don't throw - analytics failures shouldn't break PDF download
    }
  }
  
  /**
   * Check if PDF generation is supported in current browser
   * @returns {boolean} True if supported
   */
  static isSupported() {
    // Check for required features
    return !!(
      window.Blob && 
      window.URL && 
      window.URL.createObjectURL &&
      document.createElement('a').download !== undefined
    );
  }
  
  /**
   * Get estimated PDF file size
   * @param {Object} reportData - The report data
   * @returns {string} Estimated file size string
   */
  static getEstimatedFileSize(reportData) {
    const contractorCount = this.getContractorCount(reportData);
    const hasDetailedData = !!(reportData.consolidatedData && reportData.userPlan !== 'free');
    
    // Rough estimation based on content
    let baseSize = 150; // KB base size
    baseSize += contractorCount * 50; // Additional KB per contractor
    if (hasDetailedData) baseSize += 100; // Additional KB for detailed analysis
    
    if (baseSize < 1024) {
      return `${Math.round(baseSize)} KB`;
    } else {
      return `${(baseSize / 1024).toFixed(1)} MB`;
    }
  }

  /**
   * Fallback PDF generation using HTML and browser print
   * @param {Object} reportData - The data for the PDF report
   * @param {string} filename - Optional custom filename
   */
  static async generateHTMLToPDF(reportData, filename = null) {
    try {
      console.log('Using HTML-to-PDF fallback method');
      
      const loadingToast = this.showLoadingToast('Preparing PDF report...');
      
      // Generate filename
      const finalFilename = filename || this.generateFilename(reportData);
      
      // Create HTML content for PDF
      const htmlContent = this.generateHTMLReport(reportData);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load
      await new Promise(resolve => {
        printWindow.addEventListener('load', resolve);
        setTimeout(resolve, 1000); // Fallback timeout
      });
      
      // Hide loading toast
      this.hideLoadingToast(loadingToast);
      
      // Show instructions
      this.showInfoToast(`Click "Print" and select "Save as PDF" to download your report as ${finalFilename}`);
      
      // Trigger print dialog
      printWindow.print();
      
      return true;
    } catch (error) {
      console.error('HTML-to-PDF fallback failed:', error);
      this.showErrorToast('Unable to generate PDF. Please try again or contact support.');
      throw error;
    }
  }
  
  /**
   * Generate HTML content for PDF report
   * @param {Object} reportData - The report data
   * @returns {string} HTML content
   */
  static generateHTMLReport(reportData) {
    const date = new Date().toLocaleDateString();
    const contractorCount = this.getContractorCount(reportData);
    
    // Extract data for display
    const isGuestData = !!(reportData.guestAnalysisData && (!reportData.consolidatedData || reportData.userPlan === 'free'));
    const actualData = isGuestData ? 
      (reportData.guestAnalysisData.guestAnalysis || reportData.guestAnalysisData) : 
      reportData.consolidatedData;
    
    // Debug logging to help identify data structure issues
    console.log('PDF Generation - Data Structure Debug:', {
      isGuestData,
      contractorCount,
      hasGuestAnalysisData: !!reportData.guestAnalysisData,
      hasConsolidatedData: !!reportData.consolidatedData,
      userPlan: reportData.userPlan,
      actualDataKeys: actualData ? Object.keys(actualData).slice(0, 10) : [],
      guestDataStructure: isGuestData ? {
        hasContractor1: !!actualData?.Contractor_1_Information,
        hasContractor2: !!actualData?.Contractor_2_Information,
        hasComparision: !!actualData?.comparision,
        contractor1Keys: actualData?.Contractor_1_Information ? Object.keys(actualData.Contractor_1_Information) : []
      } : null,
      consolidatedDataStructure: !isGuestData && actualData ? {
        hasContractor1: !!actualData?.contractor_1,
        hasContractor2: !!actualData?.contractor_2,
        contractor1Keys: actualData?.contractor_1 ? Object.keys(actualData.contractor_1) : []
      } : null
    });
    
    return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Trakvu Contractor Analysis Report</title>
      <meta charset="UTF-8">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Inter', Arial, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #1e293b;
          background: white;
        }
        
        @media print {
          body { margin: 0; }
          .page-break { page-break-before: always; }
          .no-print { display: none; }
        }
        
        .header {
          background: linear-gradient(135deg, #0077cc 0%, #0053a6 100%);
          color: white;
          padding: 30px;
          margin-bottom: 30px;
        }
        
        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        
        .brand-name {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 5px;
        }
        
        .brand-tagline {
          font-size: 11px;
          opacity: 0.9;
        }
        
        .report-info {
          text-align: right;
        }
        
        .report-title {
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 5px;
        }
        
        .report-date {
          font-size: 10px;
          opacity: 0.8;
        }
        
        .project-title {
          font-size: 22px;
          font-weight: 700;
          margin-bottom: 8px;
        }
        
        .project-subtitle {
          font-size: 13px;
          opacity: 0.9;
        }
        
        .content {
          padding: 0 30px 30px;
        }
        
        .section {
          margin-bottom: 25px;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: 700;
          color: #0077cc;
          margin-bottom: 15px;
          padding-bottom: 5px;
          border-bottom: 2px solid #e2e8f0;
        }
        
        .contractors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 20px;
        }
        
        .contractor-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 15px;
        }
        
        .contractor-name {
          font-size: 14px;
          font-weight: 600;
          color: #0077cc;
          margin-bottom: 10px;
        }
        
        .contractor-details {
          font-size: 11px;
          line-height: 1.6;
        }
        
        .contractor-details strong {
          color: #1e293b;
        }
        
        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }
        
        .summary-item {
          background: #f8fafc;
          padding: 15px;
          border-radius: 8px;
          border-left: 4px solid #0077cc;
        }
        
        .summary-label {
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 5px;
        }
        
        .summary-value {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
        }
        
        .table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 15px;
          font-size: 11px;
        }
        
        .table th,
        .table td {
          padding: 8px 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .table th {
          background: #f8fafc;
          font-weight: 600;
          color: #475569;
        }
        
        .alert {
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 15px;
        }
        
        .alert-warning {
          background: #fef3c7;
          border-left: 4px solid #f59e0b;
          color: #92400e;
        }
        
        .alert-info {
          background: #dbeafe;
          border-left: 4px solid #3b82f6;
          color: #1e40af;
        }
        
        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          text-align: center;
          font-size: 10px;
          color: #64748b;
        }
        
                 .no-print {
           position: fixed;
           top: 10px;
           right: 10px;
           background: #ef4444;
           color: white;
           padding: 10px 15px;
           border-radius: 6px;
           font-size: 11px;
           cursor: pointer;
           z-index: 1000;
         }
         
         /* Enhanced styling for findings */
         .findings-grid {
           display: grid;
           grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
           gap: 15px;
           margin-bottom: 20px;
         }
         
         .finding-item {
           display: flex;
           align-items: flex-start;
           gap: 12px;
           padding: 15px;
           background: #f8fafc;
           border-radius: 8px;
           border-left: 4px solid #0077cc;
         }
         
         .finding-icon {
           width: 40px;
           height: 40px;
           border-radius: 50%;
           display: flex;
           align-items: center;
           justify-content: center;
           font-size: 18px;
           flex-shrink: 0;
         }
         
         .finding-content h5 {
           margin: 0 0 5px 0;
           font-size: 13px;
           font-weight: 600;
           color: #1e293b;
         }
         
         .finding-content p {
           margin: 0;
           font-size: 11px;
           color: #64748b;
           line-height: 1.4;
         }
         
         /* Enhanced contractor cards */
         .enhanced-contractor-grid {
           display: grid;
           grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
           gap: 20px;
           margin-bottom: 20px;
         }
         
         .enhanced-contractor-card {
           border: 1px solid #e2e8f0;
           border-radius: 12px;
           padding: 20px;
           background: white;
         }
         
         .contractor-header-enhanced {
           display: flex;
           align-items: center;
           gap: 15px;
           margin-bottom: 15px;
           padding-bottom: 15px;
           border-bottom: 1px solid #e2e8f0;
         }
         
         .contractor-avatar-enhanced {
           width: 50px;
           height: 50px;
           border-radius: 50%;
           background: #0077cc;
           color: white;
           display: flex;
           align-items: center;
           justify-content: center;
           font-weight: 700;
           font-size: 16px;
         }
         
         .contractor-basic-info h4 {
           margin: 0 0 5px 0;
           font-size: 16px;
           font-weight: 700;
           color: #1e293b;
         }
         
         .contractor-basic-info .contact-info {
           font-size: 10px;
           color: #64748b;
           margin: 2px 0;
         }
         
         .contractor-score-badge {
           background: #10b981;
           color: white;
           padding: 6px 12px;
           border-radius: 20px;
           font-size: 12px;
           font-weight: 600;
           margin-left: auto;
         }
         
         .contractor-details-grid {
           display: grid;
           grid-template-columns: 1fr 1fr;
           gap: 15px;
           margin-bottom: 15px;
         }
         
         .detail-section h5 {
           margin: 0 0 8px 0;
           font-size: 12px;
           font-weight: 600;
           color: #374151;
         }
         
         .detail-section .detail-value {
           font-size: 11px;
           color: #64748b;
           line-height: 1.4;
         }
         
         .risk-indicators {
           display: flex;
           gap: 8px;
           flex-wrap: wrap;
           margin-top: 10px;
         }
         
         .risk-badge {
           padding: 4px 8px;
           border-radius: 4px;
           font-size: 10px;
           font-weight: 500;
         }
         
         .risk-high {
           background: #fef2f2;
           color: #dc2626;
           border: 1px solid #fecaca;
         }
         
         .risk-medium {
           background: #fef3c7;
           color: #d97706;
           border: 1px solid #fed7aa;
         }
         
         .risk-low {
           background: #f0fdf4;
           color: #16a34a;
           border: 1px solid #bbf7d0;
         }
         
         /* Score visualization */
         .score-bars {
           margin: 15px 0;
         }
         
         .score-bar {
           display: flex;
           align-items: center;
           margin-bottom: 8px;
           gap: 10px;
         }
         
         .score-label {
           font-size: 10px;
           width: 80px;
           color: #374151;
           font-weight: 500;
         }
         
         .score-track {
           flex: 1;
           height: 12px;
           background: #e5e7eb;
           border-radius: 6px;
           overflow: hidden;
         }
         
         .score-fill {
           height: 100%;
           border-radius: 6px;
           transition: width 0.3s ease;
         }
         
         .score-value {
           font-size: 10px;
           color: #374151;
           font-weight: 600;
           width: 30px;
         }
         
         /* Allowances breakdown styling */
         .allowances-contractors {
           display: grid;
           grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
           gap: 15px;
           margin-bottom: 20px;
         }
         
         .allowance-contractor-card {
           border: 1px solid #e2e8f0;
           border-radius: 8px;
           padding: 15px;
           background: white;
         }
         
         .allowance-contractor-header {
           display: flex;
           justify-content: space-between;
           align-items: center;
           margin-bottom: 12px;
           padding-bottom: 8px;
           border-bottom: 1px solid #e2e8f0;
         }
         
         .allowance-contractor-header h5 {
           margin: 0;
           font-size: 14px;
           font-weight: 600;
           color: #1e293b;
         }
         
         .allowance-total {
           font-size: 16px;
           font-weight: 700;
           color: #059669;
         }
         
         .allowance-breakdown {
           font-size: 11px;
         }
         
         .allowance-item {
           display: flex;
           justify-content: space-between;
           margin-bottom: 6px;
           padding: 4px 0;
         }
         
         .allowance-category {
           color: #64748b;
           text-transform: capitalize;
         }
         
         .allowance-amount {
           font-weight: 600;
           color: #374151;
         }
         
         .allowances-table {
           border: 1px solid #e2e8f0;
           border-radius: 8px;
           overflow: hidden;
           margin-bottom: 20px;
         }
         
         .allowances-table-header {
           display: grid;
           grid-template-columns: 2fr repeat(var(--contractor-count, 2), 1fr) 1fr;
           background: #f8fafc;
           font-weight: 600;
           font-size: 11px;
           color: #374151;
         }
         
         .allowances-table-row {
           display: grid;
           grid-template-columns: 2fr repeat(var(--contractor-count, 2), 1fr) 1fr;
           border-top: 1px solid #e2e8f0;
           font-size: 10px;
         }
         
         .category-header,
         .contractor-header,
         .difference-header,
         .category-cell,
         .amount-cell,
         .difference-cell {
           padding: 8px 12px;
           text-align: left;
         }
         
         .contractor-header,
         .amount-cell,
         .difference-cell {
           text-align: right;
         }
         
         .category-cell {
           font-weight: 500;
           color: #374151;
           text-transform: capitalize;
         }
         
         .amount-cell {
           color: #64748b;
         }
         
         .difference-cell {
           font-weight: 600;
           color: #dc2626;
         }
         
         .allowances-insights {
           background: #fffbeb;
           border: 1px solid #fed7aa;
           border-radius: 8px;
           padding: 15px;
         }
         
         /* Risk analysis styling */
         .risk-contractor-grid {
           display: grid;
           grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
           gap: 20px;
           margin-bottom: 25px;
         }
         
         .risk-contractor-card {
           border: 1px solid #e2e8f0;
           border-radius: 12px;
           padding: 20px;
           background: white;
         }
         
         .risk-contractor-header {
           display: flex;
           justify-content: space-between;
           align-items: center;
           margin-bottom: 15px;
           padding-bottom: 10px;
           border-bottom: 1px solid #e2e8f0;
         }
         
         .risk-contractor-header h5 {
           margin: 0;
           font-size: 16px;
           font-weight: 600;
           color: #1e293b;
         }
         
         .risk-level-badge {
           padding: 6px 12px;
           border-radius: 20px;
           font-size: 11px;
           font-weight: 600;
           text-transform: uppercase;
         }
         
         .risk-level-badge.risk-high {
           background: #fef2f2;
           color: #dc2626;
           border: 1px solid #fecaca;
         }
         
         .risk-level-badge.risk-medium {
           background: #fef3c7;
           color: #d97706;
           border: 1px solid #fed7aa;
         }
         
         .risk-level-badge.risk-low {
           background: #f0fdf4;
           color: #16a34a;
           border: 1px solid #bbf7d0;
         }
         
         .risk-categories {
           font-size: 11px;
         }
         
         .risk-category {
           margin-bottom: 15px;
           padding: 12px;
           background: #f8fafc;
           border-radius: 6px;
         }
         
         .risk-category.positive {
           background: #f0fdf4;
           border-left: 4px solid #16a34a;
         }
         
         .risk-category strong {
           display: block;
           margin-bottom: 8px;
           color: #374151;
           font-size: 12px;
         }
         
         .risk-category ul {
           margin: 0;
           padding-left: 16px;
           line-height: 1.5;
         }
         
         .risk-category li {
           margin-bottom: 4px;
           color: #64748b;
         }
         
         .risk-category p {
           margin: 0;
           color: #64748b;
           font-style: italic;
         }
         
         .mitigation-section {
           margin-bottom: 20px;
           padding: 15px;
           background: #f8fafc;
           border-radius: 8px;
           border-left: 4px solid #0077cc;
         }
         
         .mitigation-section h5 {
           margin: 0 0 10px 0;
           font-size: 14px;
           font-weight: 600;
         }
         
         .mitigation-advice {
           font-size: 11px;
           color: #374151;
           line-height: 1.5;
         }
         
         .mitigation-advice ul {
           margin: 8px 0 0 0;
           padding-left: 16px;
         }
         
         .mitigation-advice li {
           margin-bottom: 6px;
         }
      </style>
    </head>
    <body>
      <div class="no-print" onclick="window.close()">Close Preview</div>
      
      <div class="header">
        <div class="header-top">
          <div class="brand-section">
            <div class="brand-name">Trakvu</div>
            <div class="brand-tagline">Smart Construction Analytics</div>
          </div>
          <div class="report-info">
            <div class="report-title">Contractor Analysis Report</div>
            <div class="report-date">Generated on ${date}</div>
          </div>
        </div>
        <div class="project-title">Construction Project Proposal Analysis</div>
        <div class="project-subtitle">${contractorCount} Contractor${contractorCount > 1 ? 's' : ''} Compared • ${reportData.userPlan || 'Free'} Plan</div>
      </div>
      
      <div class="content">
        ${this.generateExecutiveSummaryHTML(reportData, actualData)}
        ${this.generateContractorComparisonHTML(reportData, actualData)}
        ${this.generateCostAnalysisHTML(reportData, actualData)}
        ${this.generateAllowancesBreakdownHTML(reportData, actualData)}
        ${this.generateRiskAnalysisHTML(reportData, actualData)}
        ${this.generateRecommendationsHTML(reportData, actualData)}
      </div>
      
      <div class="footer">
        <p>This report was generated by Trakvu - Smart Construction Analytics Platform</p>
        <p>Visit www.trakvu.com for more information</p>
      </div>
    </body>
    </html>
    `;
  }
  
  /**
   * Generate executive summary HTML
   */
  static generateExecutiveSummaryHTML(reportData, actualData) {
    const contractorCount = this.getContractorCount(reportData);
    const isGuestData = !!(reportData.guestAnalysisData);
    
    // Get recommendation data
    const recommendation = reportData.recommendationData?.recommended_contractor ||
                          actualData?.comparision?.Recommendation?.Preferred_Contractor;
    
    // Get cost range
    const costs = this.extractCostData(reportData, actualData);
    const costRange = costs.length > 0 ? {
      min: Math.min(...costs.map(c => c.cost)),
      max: Math.max(...costs.map(c => c.cost)),
      avg: costs.reduce((sum, c) => sum + c.cost, 0) / costs.length
    } : null;
    
    // Get overall scores if available
    const overallScores = this.getOverallScores(reportData);
    
    return `
    <div class="section">
      <h2 class="section-title">Executive Summary</h2>
      
      <!-- Key Metrics Grid -->
      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">Contractors Analyzed</div>
          <div class="summary-value">${contractorCount}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Recommended</div>
          <div class="summary-value">${recommendation || 'See Analysis'}</div>
        </div>
        ${costRange ? `
        <div class="summary-item">
          <div class="summary-label">Cost Range</div>
          <div class="summary-value">$${costRange.min.toLocaleString()} - $${costRange.max.toLocaleString()}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Average Cost</div>
          <div class="summary-value">$${Math.round(costRange.avg).toLocaleString()}</div>
        </div>
        ` : ''}
      </div>
      
      <!-- Key Findings -->
      <h4 style="margin-top: 25px; margin-bottom: 15px; color: #0077cc;">Key Findings</h4>
      <div class="findings-grid">
        ${costRange ? `
        <div class="finding-item">
          <div class="finding-icon" style="background: #10b981;">💰</div>
          <div class="finding-content">
            <h5>Cost Difference</h5>
            <p>$${(costRange.max - costRange.min).toLocaleString()} difference between highest and lowest bids</p>
          </div>
        </div>
        ` : ''}
        
        ${recommendation ? `
        <div class="finding-item">
          <div class="finding-icon" style="background: #3b82f6;">🏆</div>
          <div class="finding-content">
            <h5>Top Performer</h5>
            <p>${recommendation} ranks highest in our comprehensive analysis</p>
          </div>
        </div>
        ` : ''}
        
        <div class="finding-item">
          <div class="finding-icon" style="background: #f59e0b;">⚠️</div>
          <div class="finding-content">
            <h5>Key Risks Identified</h5>
            <p>Several contractors have missing elements that could affect project success</p>
          </div>
        </div>
      </div>
      
      ${!reportData.hasActiveSubscription ? `
      <div class="alert alert-info" style="margin-top: 20px;">
        <strong>This report shows:</strong> ${isGuestData ? 'Basic analysis available on free plan' : 'Essential analysis'}. 
        Upgrade for detailed risk analysis, comprehensive scoring, and expert recommendations.
      </div>
      ` : ''}
    </div>
    `;
  }
  
  /**
   * Generate comprehensive contractor comparison HTML
   */
  static generateContractorComparisonHTML(reportData, actualData) {
    const isGuestData = !!(reportData.guestAnalysisData);
    
    let contractorsHTML = '';
    
    if (isGuestData) {
      // Guest data format with basic information
      [1, 2].forEach(num => {
        const contractor = actualData[`Contractor_${num}_Information`];
        if (contractor) {
          const companyName = contractor.Company_Name || contractor.name || `Contractor ${num}`;
          const totalCost = contractor.Financials?.total_cost || 
                           contractor.Financials?.Base_Price?.amount || 
                           contractor.total_cost || 0;
          
          // Get scoring data if available
          const contractorKey = `Contractor_${num}_Score`;
          const contractorScore = actualData?.comparision?.[contractorKey];
          const overallScore = contractorScore?.Total || 0;
          
          contractorsHTML += `
          <div class="enhanced-contractor-card">
            <div class="contractor-header-enhanced">
              <div class="contractor-avatar-enhanced">${companyName.charAt(0).toUpperCase()}</div>
              <div class="contractor-basic-info">
                <h4>${companyName}</h4>
                <div class="contact-info">
                  📧 ${contractor.Contact?.email || 'Email not provided'}
                </div>
                <div class="contact-info">
                  📞 ${contractor.Contact?.phone || 'Phone not provided'}
                </div>
              </div>
              <div class="contractor-score-badge">Score: ${overallScore}/100</div>
            </div>
            
            <div class="contractor-details-grid">
              <div class="detail-section">
                <h5>💰 Project Cost</h5>
                <div class="detail-value">$${typeof totalCost === 'number' ? totalCost.toLocaleString() : (totalCost || 'Not specified')}</div>
              </div>
              
              <div class="detail-section">
                <h5>📋 License</h5>
                <div class="detail-value">${contractor.License?.license_number || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>🏗️ Experience</h5>
                <div class="detail-value">${contractor.Experience?.years_of_experience || 'Not specified'} years</div>
              </div>
              
              <div class="detail-section">
                <h5>🎯 Specialization</h5>
                <div class="detail-value">${contractor.Specialization || 'General Construction'}</div>
              </div>
            </div>
            
            ${contractorScore?.Breakdown ? `
            <div class="score-bars">
              <h5>Performance Breakdown</h5>
              ${Object.entries(contractorScore.Breakdown).slice(0, 5).map(([category, score]) => `
              <div class="score-bar">
                <div class="score-label">${category.replace(/_/g, ' ')}</div>
                <div class="score-track">
                  <div class="score-fill" style="width: ${(score/15)*100}%; background: ${score >= 12 ? '#10b981' : score >= 8 ? '#f59e0b' : '#ef4444'};"></div>
                </div>
                <div class="score-value">${score}/15</div>
              </div>
              `).join('')}
            </div>
            ` : ''}
            
            ${contractor.Risk_Alerts && contractor.Risk_Alerts.length > 0 ? `
            <div class="risk-indicators">
              ${contractor.Risk_Alerts.slice(0, 3).map(alert => `
              <span class="risk-badge risk-medium">${alert.title}</span>
              `).join('')}
            </div>
            ` : ''}
          </div>
          `;
        }
      });
    } else {
      // Full consolidated data format with comprehensive information
      [1, 2, 3, 4, 5].forEach(num => {
        const contractor = actualData[`contractor_${num}`];
        if (contractor && contractor.contractor) {
          const contractorInfo = contractor.contractor;
          const totalCost = contractor.payment_terms?.total_cost || '0';
          const costNumber = typeof totalCost === 'string' ? 
            parseFloat(totalCost.replace(/[\$,]/g, '')) : totalCost;
          
          // Get evaluation data for this contractor
          const evalKey = `contractor_${num}`;
          const evaluation = reportData.evaluationData?.[evalKey];
          
          // Calculate overall score from evaluation categories
          let overallScore = 0;
          if (evaluation) {
            const categories = [
              'communication_and_accountability',
              'permit_information',
              'logistics_and_execution_plan',
              'innovation_and_value_adds',
              'timeline_and_schedule_factors',
              'scope_and_methodology',
              'safety_measurements',
              'labor_and_workforce',
              'documentation_and_recordkeeping',
              'contracting_behavior'
            ];
            
            const validScores = categories
              .map(cat => evaluation[cat]?.score)
              .filter(score => score !== undefined && score !== null);
            
            overallScore = validScores.length > 0 ? 
              Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
          }
          
          contractorsHTML += `
          <div class="enhanced-contractor-card">
            <div class="contractor-header-enhanced">
              <div class="contractor-avatar-enhanced">${contractorInfo.contractor_name.charAt(0).toUpperCase()}</div>
              <div class="contractor-basic-info">
                <h4>${contractorInfo.contractor_name}</h4>
                <div class="contact-info">
                  📧 ${contractorInfo.contractor_email || 'Email not provided'}
                </div>
                <div class="contact-info">
                  📞 ${contractorInfo.contractor_phone || 'Phone not provided'}
                </div>
                <div class="contact-info">
                  📍 ${contractorInfo.contractor_address || 'Address not provided'}
                </div>
              </div>
              <div class="contractor-score-badge">Score: ${overallScore}/10</div>
            </div>
            
            <div class="contractor-details-grid">
              <div class="detail-section">
                <h5>💰 Project Cost</h5>
                <div class="detail-value">$${costNumber?.toLocaleString() || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>📋 License</h5>
                <div class="detail-value">#${contractor.license_details?.license_number || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>🛡️ Insurance</h5>
                <div class="detail-value">${contractor.insurance_details?.["liability insurance"]?.coverage || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>⏱️ Timeline</h5>
                <div class="detail-value">${contractor.timeline_details?.estimated_total_project_duration || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>💳 Payment Terms</h5>
                <div class="detail-value">${contractor.payment_terms?.pricing_structure || 'Not specified'}</div>
              </div>
              
              <div class="detail-section">
                <h5>🏆 Certifications</h5>
                <div class="detail-value">${contractor.qualifications?.join(', ') || 'None specified'}</div>
              </div>
            </div>
            
            ${evaluation ? `
            <div class="score-bars">
              <h5>Performance Evaluation</h5>
              ${[
                { key: 'communication_and_accountability', label: 'Communication' },
                { key: 'safety_measurements', label: 'Safety' },
                { key: 'scope_and_methodology', label: 'Scope & Method' },
                { key: 'timeline_and_schedule_factors', label: 'Timeline' },
                { key: 'contracting_behavior', label: 'Behavior' }
              ].map(({ key, label }) => {
                const score = evaluation[key]?.score || 0;
                return `
                <div class="score-bar">
                  <div class="score-label">${label}</div>
                  <div class="score-track">
                    <div class="score-fill" style="width: ${(score/10)*100}%; background: ${score >= 8 ? '#10b981' : score >= 5 ? '#f59e0b' : '#ef4444'};"></div>
                  </div>
                  <div class="score-value">${score}/10</div>
                </div>
                `;
              }).join('')}
            </div>
            ` : ''}
            
            ${evaluation?.risk && evaluation.risk.length > 0 ? `
            <div class="risk-indicators">
              <h5 style="width: 100%; margin-bottom: 8px;">⚠️ Risk Factors</h5>
              ${evaluation.risk.slice(0, 3).map(risk => `
              <span class="risk-badge risk-high">${risk}</span>
              `).join('')}
            </div>
            ` : ''}
            
            ${evaluation?.strengths && evaluation.strengths.length > 0 ? `
            <div style="margin-top: 15px;">
              <h5 style="font-size: 12px; color: #059669; margin-bottom: 5px;">✅ Key Strengths</h5>
              <div style="font-size: 10px; color: #064e3b;">
                ${evaluation.strengths.slice(0, 2).map(strength => `• ${strength}`).join('<br>')}
              </div>
            </div>
            ` : ''}
          </div>
          `;
        }
      });
    }
    
    if (!contractorsHTML) {
      contractorsHTML = `
      <div class="enhanced-contractor-card">
        <div class="contractor-header-enhanced">
          <div class="contractor-avatar-enhanced">?</div>
          <div class="contractor-basic-info">
            <h4>No Contractor Data Available</h4>
            <div class="contact-info">Please ensure contractor data is properly loaded</div>
          </div>
        </div>
      </div>
      `;
    }
    
    return `
    <div class="section">
      <h2 class="section-title">Comprehensive Contractor Analysis</h2>
      <div class="enhanced-contractor-grid">
        ${contractorsHTML}
      </div>
    </div>
    `;
  }
  
  /**
   * Generate cost analysis HTML
   */
  static generateCostAnalysisHTML(reportData, actualData) {
    const isGuestData = !!(reportData.guestAnalysisData);
    
    if (isGuestData) {
      const costs = [];
      [1, 2].forEach(num => {
        const contractor = actualData[`Contractor_${num}_Information`];
        if (contractor) {
          const companyName = contractor.Company_Name || contractor.name || `Contractor ${num}`;
          let totalCost = contractor.Financials?.total_cost || 
                         contractor.Financials?.Base_Price?.amount || 
                         contractor.total_cost;
          
          // Parse cost if it's a string
          if (typeof totalCost === 'string') {
            totalCost = parseFloat(totalCost.replace(/[\$,]/g, ''));
          }
          
          if (totalCost && totalCost > 0) {
            costs.push({
              name: companyName,
              cost: totalCost
            });
          }
        }
      });
      
      if (costs.length === 0) {
        return `
        <div class="section">
          <h2 class="section-title">Cost Analysis</h2>
          <div class="alert alert-info">
            <strong>No Cost Data Available:</strong> Cost information was not found in the analysis data.
          </div>
        </div>
        `;
      }
      
      const minCost = Math.min(...costs.map(c => c.cost));
      const maxCost = Math.max(...costs.map(c => c.cost));
      const costDifference = maxCost - minCost;
      const avgCost = costs.reduce((sum, c) => sum + c.cost, 0) / costs.length;
      
      return `
      <div class="section">
        <h2 class="section-title">Cost Analysis</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <div class="summary-label">Lowest Bid</div>
            <div class="summary-value">$${minCost.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Highest Bid</div>
            <div class="summary-value">$${maxCost.toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Average</div>
            <div class="summary-value">$${Math.round(avgCost).toLocaleString()}</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">Cost Difference</div>
            <div class="summary-value">$${costDifference.toLocaleString()}</div>
          </div>
        </div>
        
                  <!-- Visual Cost Comparison Chart -->
          <div style="margin: 20px 0; padding: 20px; background: #f8fafc; border-radius: 8px;">
            <h4 style="margin-bottom: 15px; color: #1e293b;">Cost Comparison Chart</h4>
            ${costs.map(contractor => {
              const barWidth = Math.round((contractor.cost / maxCost) * 100);
              const isLowest = contractor.cost === minCost;
              const isHighest = contractor.cost === maxCost;
              
              return `
              <div style="margin-bottom: 12px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="font-weight: 600; color: #374151;">${contractor.name}</span>
                  <span style="font-weight: 600; color: #059669;">$${contractor.cost.toLocaleString()}</span>
                </div>
                <div style="background: #e5e7eb; height: 20px; border-radius: 10px; overflow: hidden;">
                  <div style="
                    width: ${barWidth}%; 
                    height: 100%; 
                    background: ${isLowest ? '#10b981' : isHighest ? '#ef4444' : '#0077cc'}; 
                    border-radius: 10px;
                    position: relative;
                  "></div>
                </div>
                <div style="font-size: 11px; color: #6b7280; margin-top: 2px;">
                  ${isLowest ? '🏆 Lowest bid' : isHighest ? '⚠️ Highest bid' : ''}
                  ${contractor.cost !== minCost ? `(+$${(contractor.cost - minCost).toLocaleString()})` : ''}
                </div>
              </div>
              `;
            }).join('')}
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Total Cost</th>
                <th>Difference from Lowest</th>
                <th>% Above Lowest</th>
              </tr>
            </thead>
            <tbody>
              ${costs.map(contractor => `
              <tr>
                <td>${contractor.name}</td>
                <td>$${contractor.cost.toLocaleString()}</td>
                <td>$${(contractor.cost - minCost).toLocaleString()}</td>
                <td>${minCost > 0 ? (((contractor.cost - minCost) / minCost) * 100).toFixed(1) : '0'}%</td>
              </tr>
              `).join('')}
            </tbody>
          </table>
      </div>
      `;
    }
    
    // For consolidated data
    if (actualData) {
      const costs = [];
      [1, 2, 3, 4, 5].forEach(num => {
        const contractor = actualData[`contractor_${num}`];
        if (contractor && contractor.contractor && contractor.payment_terms) {
          const contractorName = contractor.contractor.contractor_name;
          let totalCost = contractor.payment_terms.total_cost;
          
          if (typeof totalCost === 'string') {
            totalCost = parseFloat(totalCost.replace(/[\$,]/g, ''));
          }
          
          if (totalCost && totalCost > 0) {
            costs.push({
              name: contractorName,
              cost: totalCost
            });
          }
        }
      });
      
      if (costs.length > 0) {
        const minCost = Math.min(...costs.map(c => c.cost));
        const maxCost = Math.max(...costs.map(c => c.cost));
        const costDifference = maxCost - minCost;
        const avgCost = costs.reduce((sum, c) => sum + c.cost, 0) / costs.length;
        
        return `
        <div class="section">
          <h2 class="section-title">Cost Analysis</h2>
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-label">Lowest Bid</div>
              <div class="summary-value">$${minCost.toLocaleString()}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Highest Bid</div>
              <div class="summary-value">$${maxCost.toLocaleString()}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Average</div>
              <div class="summary-value">$${Math.round(avgCost).toLocaleString()}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Cost Spread</div>
              <div class="summary-value">$${costDifference.toLocaleString()}</div>
            </div>
          </div>
          
          <!-- Visual Cost Comparison Chart for Consolidated Data -->
          <div style="margin: 20px 0; padding: 20px; background: #f8fafc; border-radius: 8px;">
            <h4 style="margin-bottom: 15px; color: #1e293b;">Cost Comparison Chart</h4>
            ${costs.map(contractor => {
              const barWidth = Math.round((contractor.cost / maxCost) * 100);
              const isLowest = contractor.cost === minCost;
              const isHighest = contractor.cost === maxCost;
              
              return `
              <div style="margin-bottom: 12px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="font-weight: 600; color: #374151;">${contractor.name}</span>
                  <span style="font-weight: 600; color: #059669;">$${contractor.cost.toLocaleString()}</span>
                </div>
                <div style="background: #e5e7eb; height: 20px; border-radius: 10px; overflow: hidden;">
                  <div style="
                    width: ${barWidth}%; 
                    height: 100%; 
                    background: ${isLowest ? '#10b981' : isHighest ? '#ef4444' : '#0077cc'}; 
                    border-radius: 10px;
                    position: relative;
                  "></div>
                </div>
                <div style="font-size: 11px; color: #6b7280; margin-top: 2px;">
                  ${isLowest ? '🏆 Lowest bid' : isHighest ? '⚠️ Highest bid' : ''}
                  ${contractor.cost !== minCost ? `(+$${(contractor.cost - minCost).toLocaleString()})` : ''}
                </div>
              </div>
              `;
            }).join('')}
          </div>
          
          <table class="table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Total Cost</th>
                <th>Difference from Lowest</th>
                <th>% Above Lowest</th>
              </tr>
            </thead>
            <tbody>
              ${costs.map(contractor => `
              <tr>
                <td>${contractor.name}</td>
                <td>$${contractor.cost.toLocaleString()}</td>
                <td>$${(contractor.cost - minCost).toLocaleString()}</td>
                <td>${minCost > 0 ? (((contractor.cost - minCost) / minCost) * 100).toFixed(1) : '0'}%</td>
              </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="alert alert-info">
            <strong>Detailed Analysis:</strong> Complete plan includes allowances breakdown, payment terms comparison, and risk assessment.
          </div>
        </div>
        `;
      }
    }
    
    return `
    <div class="section">
      <h2 class="section-title">Cost Analysis</h2>
      <div class="alert alert-info">
        <strong>Cost Analysis Unavailable:</strong> No contractor cost data found. Please ensure your analysis includes pricing information.
      </div>
    </div>
    `;
  }
  
  /**
   * Generate recommendations HTML
   */
  static generateRecommendationsHTML(reportData, actualData) {
    const isGuestData = !!(reportData.guestAnalysisData);
    
    if (isGuestData) {
      // Check for recommendation in guest data structure
      const recommendation = actualData.comparision?.Recommendation || 
                           actualData.Recommendation ||
                           actualData.recommendation;
      
      if (recommendation) {
        const preferredContractor = recommendation.Preferred_Contractor || 
                                  recommendation.preferred_contractor ||
                                  recommendation.contractor;
        const reason = recommendation.Reason || 
                      recommendation.reason ||
                      recommendation.reasoning;
        
        // Get contractor scores for additional context
        const contractor1Score = actualData.comparision?.Contractor_1_Score?.Total || 0;
        const contractor2Score = actualData.comparision?.Contractor_2_Score?.Total || 0;
        
        return `
        <div class="section">
          <h2 class="section-title">Recommendation</h2>
          <div class="alert alert-info">
            <h4>Recommended Contractor: ${preferredContractor || 'Not specified'}</h4>
            ${reason ? `<p><strong>Reasoning:</strong> ${reason}</p>` : ''}
          </div>
          
          ${(contractor1Score > 0 || contractor2Score > 0) ? `
          <h4>Contractor Scores</h4>
          <table class="table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Overall Score</th>
                <th>Performance Rating</th>
              </tr>
            </thead>
            <tbody>
              ${contractor1Score > 0 ? `
              <tr>
                <td>Contractor 1</td>
                <td>${contractor1Score}/100</td>
                <td>${contractor1Score >= 80 ? 'Excellent' : contractor1Score >= 60 ? 'Good' : contractor1Score >= 40 ? 'Fair' : 'Poor'}</td>
              </tr>
              ` : ''}
              ${contractor2Score > 0 ? `
              <tr>
                <td>Contractor 2</td>
                <td>${contractor2Score}/100</td>
                <td>${contractor2Score >= 80 ? 'Excellent' : contractor2Score >= 60 ? 'Good' : contractor2Score >= 40 ? 'Fair' : 'Poor'}</td>
              </tr>
              ` : ''}
            </tbody>
          </table>
          ` : ''}
        </div>
        `;
      }
      
      return `
      <div class="section">
        <h2 class="section-title">Recommendation</h2>
        <div class="alert alert-info">
          <strong>No Specific Recommendation:</strong> The analysis did not include a specific contractor recommendation.
        </div>
      </div>
      `;
    }
    
    // For consolidated data
    if (reportData.recommendationData) {
      const rec = reportData.recommendationData;
      const recommendedContractor = rec.recommended_contractor;
      const reasoning = rec.reasoning;
      
      return `
      <div class="section">
        <h2 class="section-title">Expert Recommendation</h2>
        <div class="alert alert-info">
          <h4>Recommended Contractor: ${recommendedContractor || 'Not specified'}</h4>
          ${reasoning ? `<p><strong>Reasoning:</strong> ${reasoning}</p>` : ''}
        </div>
        
        ${rec.ranking && rec.ranking.length > 0 ? `
        <h4>Contractor Rankings</h4>
        <table class="table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Contractor</th>
              <th>Alignment Score</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            ${rec.ranking.map((contractor, index) => `
            <tr>
              <td>#${index + 1}</td>
              <td>${contractor.contractor_name}</td>
              <td>${contractor.alignment_score}%</td>
              <td>${contractor.alignment_score >= 90 ? 'Excellent' : contractor.alignment_score >= 75 ? 'Good' : contractor.alignment_score >= 60 ? 'Fair' : 'Poor'}</td>
            </tr>
            `).join('')}
          </tbody>
        </table>
        ` : ''}
      </div>
      `;
    }
    
    return `
    <div class="section">
      <h2 class="section-title">Recommendations</h2>
      <div class="alert alert-info">
        <strong>Expert Recommendations:</strong> Detailed recommendations and risk analysis are available with paid plans. Upgrade to get personalized contractor recommendations.
      </div>
    </div>
    `;
  }
  
  /**
   * Extract cost data from report
   */
  static extractCostData(reportData, actualData) {
    const costs = [];
    const isGuestData = !!(reportData.guestAnalysisData);
    
    if (isGuestData) {
      [1, 2].forEach(num => {
        const contractor = actualData[`Contractor_${num}_Information`];
        if (contractor) {
          const companyName = contractor.Company_Name || contractor.name || `Contractor ${num}`;
          let totalCost = contractor.Financials?.total_cost || 
                         contractor.Financials?.Base_Price?.amount || 
                         contractor.total_cost;
          
          if (typeof totalCost === 'string') {
            totalCost = parseFloat(totalCost.replace(/[\$,]/g, ''));
          }
          
          if (totalCost && totalCost > 0) {
            costs.push({ name: companyName, cost: totalCost });
          }
        }
      });
    } else {
      [1, 2, 3, 4, 5].forEach(num => {
        const contractor = actualData[`contractor_${num}`];
        if (contractor && contractor.contractor && contractor.payment_terms) {
          const contractorName = contractor.contractor.contractor_name;
          let totalCost = contractor.payment_terms.total_cost;
          
          if (typeof totalCost === 'string') {
            totalCost = parseFloat(totalCost.replace(/[\$,]/g, ''));
          }
          
          if (totalCost && totalCost > 0) {
            costs.push({ name: contractorName, cost: totalCost });
          }
        }
      });
    }
    
    return costs;
  }
  
  /**
   * Get overall scores from evaluation data
   */
  static getOverallScores(reportData) {
    if (!reportData.evaluationData) return [];
    
    const scores = [];
    Object.keys(reportData.evaluationData).forEach(key => {
      if (key.startsWith('contractor_')) {
        const contractor = reportData.evaluationData[key];
        if (contractor.contractor_name) {
          // Calculate average score from all categories
          const categories = [
            'communication_and_accountability',
            'permit_information',
            'logistics_and_execution_plan',
            'innovation_and_value_adds',
            'timeline_and_schedule_factors',
            'scope_and_methodology',
            'safety_measurements',
            'labor_and_workforce',
            'documentation_and_recordkeeping',
            'contracting_behavior'
          ];
          
          const validScores = categories
            .map(cat => contractor[cat]?.score)
            .filter(score => score !== undefined && score !== null);
          
          const avgScore = validScores.length > 0 ? 
            Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
          
          scores.push({
            name: contractor.contractor_name,
            score: avgScore,
            maxScore: 10
          });
        }
      }
    });
    
    return scores;
  }

  /**
   * Generate allowances breakdown HTML
   */
  static generateAllowancesBreakdownHTML(reportData, actualData) {
    const isGuestData = !!(reportData.guestAnalysisData);
    
    if (isGuestData) {
      return `
      <div class="section">
        <h2 class="section-title">Allowances Breakdown</h2>
        <div class="alert alert-info">
          <strong>Detailed Allowances:</strong> Available with paid plans. Upgrade to see comprehensive cost breakdowns and allowances analysis.
        </div>
      </div>
      `;
    }
    
    // Extract allowances from consolidated data
    const allowancesData = [];
    [1, 2, 3, 4, 5].forEach(num => {
      const contractor = actualData[`contractor_${num}`];
      if (contractor && contractor.contractor && contractor.allowances) {
        const allowances = contractor.allowances.direct_costs || {};
        const contractorName = contractor.contractor.contractor_name;
        
        allowancesData.push({
          name: contractorName,
          allowances: allowances,
          total: Object.values(allowances).reduce((sum, val) => {
            const numVal = typeof val === 'number' ? val : parseFloat(val?.toString().replace(/[\$,]/g, '') || '0');
            return sum + (numVal || 0);
          }, 0)
        });
      }
    });
    
    if (allowancesData.length === 0) {
      return `
      <div class="section">
        <h2 class="section-title">Allowances Breakdown</h2>
        <div class="alert alert-info">
          <strong>No Allowances Data:</strong> Detailed allowances information not available in the current analysis.
        </div>
      </div>
      `;
    }
    
    // Get all unique allowance categories
    const allCategories = new Set();
    allowancesData.forEach(contractor => {
      Object.keys(contractor.allowances).forEach(category => {
        allCategories.add(category);
      });
    });
    
    const categories = Array.from(allCategories).slice(0, 10); // Top 10 categories
    
    return `
    <div class="section">
      <h2 class="section-title">Detailed Allowances Breakdown</h2>
      
      <!-- Contractor Totals -->
      <div class="allowances-summary">
        <h4 style="margin-bottom: 15px; color: #0077cc;">Total Allowances by Contractor</h4>
        <div class="allowances-contractors">
          ${allowancesData.map(contractor => `
          <div class="allowance-contractor-card">
            <div class="allowance-contractor-header">
              <h5>${contractor.name}</h5>
              <div class="allowance-total">$${contractor.total.toLocaleString()}</div>
            </div>
            <div class="allowance-breakdown">
              ${Object.entries(contractor.allowances).slice(0, 5).map(([category, amount]) => {
                const cleanAmount = typeof amount === 'number' ? amount : parseFloat(amount?.toString().replace(/[\$,]/g, '') || '0');
                return `
                <div class="allowance-item">
                  <span class="allowance-category">${category.replace(/_/g, ' ')}</span>
                  <span class="allowance-amount">$${cleanAmount.toLocaleString()}</span>
                </div>
                `;
              }).join('')}
              ${Object.keys(contractor.allowances).length > 5 ? `
              <div class="allowance-item">
                <span class="allowance-category">... and ${Object.keys(contractor.allowances).length - 5} more</span>
              </div>
              ` : ''}
            </div>
          </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Category Comparison -->
      <h4 style="margin: 25px 0 15px 0; color: #0077cc;">Category-by-Category Comparison</h4>
      <div class="allowances-table">
        <div class="allowances-table-header">
          <div class="category-header">Category</div>
          ${allowancesData.map(contractor => `
          <div class="contractor-header">${contractor.name.substring(0, 12)}${contractor.name.length > 12 ? '...' : ''}</div>
          `).join('')}
          <div class="difference-header">Difference</div>
        </div>
        
        ${categories.map(category => {
          const amounts = allowancesData.map(contractor => {
            const amount = contractor.allowances[category] || 0;
            return typeof amount === 'number' ? amount : parseFloat(amount?.toString().replace(/[\$,]/g, '') || '0');
          });
          
          const maxAmount = Math.max(...amounts);
          const minAmount = Math.min(...amounts.filter(a => a > 0));
          const difference = maxAmount - minAmount;
          
          return `
          <div class="allowances-table-row">
            <div class="category-cell">${category.replace(/_/g, ' ')}</div>
            ${amounts.map((amount, index) => `
            <div class="amount-cell">
              ${amount > 0 ? `$${amount.toLocaleString()}` : '-'}
              ${amount === maxAmount && amount > 0 ? ' 🔴' : amount === minAmount && amount > 0 ? ' 🟢' : ''}
            </div>
            `).join('')}
            <div class="difference-cell">
              ${difference > 0 ? `$${difference.toLocaleString()}` : '-'}
            </div>
          </div>
          `;
        }).join('')}
      </div>
      
      <div class="allowances-insights">
        <h4 style="color: #f59e0b; margin-bottom: 10px;">💡 Key Insights</h4>
        <ul style="margin: 0; padding-left: 20px; font-size: 11px; color: #374151;">
          <li>🟢 indicates lowest cost in category, 🔴 indicates highest cost</li>
          <li>Large differences may indicate varying quality levels or missing items</li>
          <li>Categories with '-' indicate the contractor didn't include that allowance</li>
          <li>Total allowances difference: $${(Math.max(...allowancesData.map(c => c.total)) - Math.min(...allowancesData.map(c => c.total))).toLocaleString()}</li>
        </ul>
      </div>
    </div>
    `;
  }
  
  /**
   * Generate risk analysis HTML
   */
  static generateRiskAnalysisHTML(reportData, actualData) {
    if (!reportData.evaluationData) {
      return `
      <div class="section">
        <h2 class="section-title">Risk Analysis</h2>
        <div class="alert alert-info">
          <strong>Risk Analysis:</strong> Available with paid plans. Upgrade to see comprehensive risk assessment and mitigation strategies.
        </div>
      </div>
      `;
    }
    
    const riskData = [];
    
    // Extract risk data from evaluation
    Object.keys(reportData.evaluationData).forEach(key => {
      if (key.startsWith('contractor_')) {
        const contractor = reportData.evaluationData[key];
        if (contractor.contractor_name) {
          const risks = contractor.risk || [];
          const missingInfo = contractor.missing_information || [];
          const weaknesses = contractor.weaknesses || [];
          
          // Calculate risk level based on scores
          const lowScoreCategories = [
            'communication_and_accountability',
            'permit_information',
            'logistics_and_execution_plan',
            'safety_measurements',
            'contracting_behavior'
          ].filter(cat => (contractor[cat]?.score || 0) < 4);
          
          let riskLevel = 'Low';
          if (risks.length > 3 || lowScoreCategories.length > 2) {
            riskLevel = 'High';
          } else if (risks.length > 1 || lowScoreCategories.length > 0) {
            riskLevel = 'Medium';
          }
          
          riskData.push({
            name: contractor.contractor_name,
            riskLevel,
            risks: risks.slice(0, 5),
            missingInfo: missingInfo.slice(0, 3),
            weaknesses: weaknesses.slice(0, 3),
            lowScoreCategories,
            strengths: contractor.strengths || []
          });
        }
      }
    });
    
    if (riskData.length === 0) {
      return `
      <div class="section">
        <h2 class="section-title">Risk Analysis</h2>
        <div class="alert alert-info">
          <strong>No Risk Data:</strong> Risk analysis information not available in the current evaluation.
        </div>
      </div>
      `;
    }
    
    return `
    <div class="section">
      <h2 class="section-title">Comprehensive Risk Analysis</h2>
      
      <!-- Risk Overview -->
      <div class="risk-overview">
        <h4 style="margin-bottom: 15px; color: #dc2626;">⚠️ Risk Assessment Summary</h4>
        <div class="risk-contractor-grid">
          ${riskData.map(contractor => `
          <div class="risk-contractor-card">
            <div class="risk-contractor-header">
              <h5>${contractor.name}</h5>
              <span class="risk-level-badge risk-${contractor.riskLevel.toLowerCase()}">
                ${contractor.riskLevel} Risk
              </span>
            </div>
            
            <div class="risk-categories">
              <div class="risk-category">
                <strong>🚨 Key Risks (${contractor.risks.length})</strong>
                ${contractor.risks.length > 0 ? `
                <ul>
                  ${contractor.risks.map(risk => `<li>${risk}</li>`).join('')}
                </ul>
                ` : '<p>No specific risks identified</p>'}
              </div>
              
              <div class="risk-category">
                <strong>❓ Missing Information (${contractor.missingInfo.length})</strong>
                ${contractor.missingInfo.length > 0 ? `
                <ul>
                  ${contractor.missingInfo.map(info => `<li>${info}</li>`).join('')}
                </ul>
                ` : '<p>No missing information</p>'}
              </div>
              
              ${contractor.lowScoreCategories.length > 0 ? `
              <div class="risk-category">
                <strong>📉 Areas of Concern</strong>
                <ul>
                  ${contractor.lowScoreCategories.map(cat => `<li>${cat.replace(/_/g, ' ')}</li>`).join('')}
                </ul>
              </div>
              ` : ''}
              
              ${contractor.strengths.length > 0 ? `
              <div class="risk-category positive">
                <strong>✅ Key Strengths</strong>
                <ul>
                  ${contractor.strengths.slice(0, 2).map(strength => `<li>${strength}</li>`).join('')}
                </ul>
              </div>
              ` : ''}
            </div>
          </div>
          `).join('')}
        </div>
      </div>
      
      <!-- Risk Mitigation Recommendations -->
      <div class="risk-mitigation">
        <h4 style="margin: 25px 0 15px 0; color: #0077cc;">🛡️ Risk Mitigation Recommendations</h4>
        
        ${riskData.filter(c => c.riskLevel === 'High').length > 0 ? `
        <div class="mitigation-section">
          <h5 style="color: #dc2626;">High-Risk Contractors</h5>
          <div class="mitigation-advice">
            <p><strong>Before proceeding with high-risk contractors:</strong></p>
            <ul>
              <li>Request additional documentation and references</li>
              <li>Require larger insurance coverage and bonding</li>
              <li>Implement stricter milestone-based payments</li>
              <li>Consider requiring a completion guarantee</li>
              <li>Schedule more frequent progress meetings</li>
            </ul>
          </div>
        </div>
        ` : ''}
        
        <div class="mitigation-section">
          <h5 style="color: #059669;">General Recommendations</h5>
          <div class="mitigation-advice">
            <ul>
              <li><strong>Communication:</strong> Establish clear communication protocols from day one</li>
              <li><strong>Documentation:</strong> Require detailed project documentation and regular updates</li>
              <li><strong>Timeline:</strong> Build in buffer time for permit delays and unexpected issues</li>
              <li><strong>Payment:</strong> Use milestone-based payments tied to completed work</li>
              <li><strong>Quality:</strong> Schedule regular quality inspections throughout the project</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    `;
  }

  /**
   * Show info toast notification
   * @param {string} message - Info message
   */
  static showInfoToast(message) {
    const toastId = 'pdf-info-' + Date.now();
    
    const infoDiv = document.createElement('div');
    infoDiv.id = toastId;
    infoDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #3b82f6;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: Inter, sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: 400px;
      line-height: 1.4;
    `;
    infoDiv.innerHTML = `
      <i class="fas fa-info-circle"></i>
      ${message}
    `;
    
    document.body.appendChild(infoDiv);
    
    // Auto remove after 8 seconds
    setTimeout(() => {
      infoDiv.remove();
    }, 8000);
  }
}

export default PDFService; 